/**
 * The sound palette.
 *
 * Six sound effects from Mixkit's "High Tech" set, licensed under the Mixkit
 * Free License (free for commercial and non-commercial use, no attribution
 * required; the files may not be redistributed on their own).
 *
 * Every file was trimmed to exactly the span the site plays, peak-normalised
 * to a common level so no cue is noticeably louder than another, and
 * re-encoded — UI blips to 64 kbps mono, sweeps and the title cue to stereo,
 * because stereo width is the whole point of a sweep and irrelevant to a
 * click. The set totals ~150 kB and is fetched only for visitors who have
 * sound switched on.
 *
 * `gain` is the last word on relative loudness. The files are normalised to
 * the same peak, but peak is not loudness — a 150ms tick and a 7-second
 * riser at identical peaks are nothing alike to the ear, so these values are
 * set by listening, not by measurement.
 */

export interface Cue {
  /** Path under `public/sfx`. */
  src: string
  /** Playback level, 0–1, before the master volume. */
  gain: number
  /** Minimum gap between two plays of this cue, in ms. Stops machine-gunning. */
  throttle?: number
}

export const CUES = {
  /**
   * Mixkit "Tech click", cut to 150ms.
   *
   * Cut this short on purpose: the source has a tail, and a tail that
   * re-triggers every time the pointer crosses a link turns a dense
   * navigation rail into mush. What is left is the transient — the part the
   * ear actually reads as "something is under the cursor".
   */
  hover: { src: 'hover.mp3', gain: 0.26, throttle: 130 },

  /** Mixkit "High tech bleep". The press itself. */
  click: { src: 'click.mp3', gain: 0.45, throttle: 45 },

  /** Mixkit "Tech transitions", first 1.3s — the page transition cue. */
  navigate: { src: 'transition.mp3', gain: 0.5, throttle: 140 },

  /**
   * Mixkit "Sci fi sweep", for backward navigation.
   *
   * A sweep reads as travelling rather than arriving, which is the right
   * feeling for retracing your steps, and it keeps back audibly distinct
   * from forward without a second transition sample.
   */
  back: { src: 'back.mp3', gain: 0.5, throttle: 140 },

  /** Mixkit "High tech bleep confirmation", for committing a filter. */
  select: { src: 'select.mp3', gain: 0.45, throttle: 90 },

  /**
   * Mixkit "Futuristic space intro" — a riser resolving into an impact.
   *
   * Cut to end when the sound does. The source runs on for another 1.2
   * seconds of near-silent reverb after the hit, and the intro scene paces
   * the hero's arrival across the whole file: left in, the picture would
   * still be fading up over a second after the last thing anyone can hear.
   */
  intro: { src: 'intro.mp3', gain: 0.7 },
} as const satisfies Record<string, Cue>

export type CueName = keyof typeof CUES

/**
 * Where the title cue's impact lands, in seconds from the start of the file.
 *
 * Measured off the encoded asset, not guessed: the 20ms RMS envelope peaks
 * at 6.06s, which is the downbeat the riser has been building towards. The
 * intro scene lands its zoom and drops in its copy on exactly this moment,
 * so the picture settles on the hit rather than near it.
 *
 * If `public/sfx/intro.mp3` is ever replaced, re-measure this.
 */
export const INTRO_IMPACT = 6.06

/**
 * Total length of the title cue, in seconds.
 *
 * The intro scene's entry — the fade, the zoom, the copy — is paced across
 * exactly this, so the hero finishes arriving as the cue dies away.
 */
export const INTRO_DURATION = 6.95
